import React, { useContext, useState } from 'react';
import LinkedCompaniesGrid from '../../organisms/LinkedCompaniesGrid/view';
import ProviderStatus from '../../molecules/ProviderStatus/view';
import ApiKeyManager from '../../organisms/ApiKeyManager/ApiKeyManager';
import ButtonGroup from '../../molecules/buttonGroup/view';
import ModalBtn from '../../molecules/modalButton/view';
import { useProviderHealth } from '../../../usecases/useProviderHealth';
import { Key } from 'lucide-react';
import { roleContext } from '../../../context/role/context';
import PipelineDraftsManager from '../../organisms/PipelineDraftsManager/view';
import Modal from '../../atoms/modal/view';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { useAccountantHub } from '../../../usecases/useAccountantHub';
import { useOnboardingTour } from '../../../hooks/useOnboardingTour';
import OfflineClientFormModal from '../../organisms/OfflineClientFormModal/view';
import ButtonBasic from '../../atoms/buttonBasic/view';
import { setCookie, deleteCookie } from '../../../utils/cookies';
import './style.css';

/**
 * AccountantHubPage Template/Page
 * Main landing for accountants to manage invitations, switch contexts, and open drafts modals.
 */
function AccountantHubPage() {
    useOnboardingTour();
    const { status: healthStatus } = useProviderHealth();
    const { profile, isInpersonated, unpersonate, suscripcion } = useContext(roleContext);
    const accountantHub = useAccountantHub();

    // Modal state for pending drafts review
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);

    // Hook to refresh grid counts on modal close
    const [gridKey, setGridKey] = useState(0);

    const handleOpenDraftsModal = (companyId) => {
        // Backup current cid
        const currentCid = localStorage.getItem('cid') || '';
        localStorage.setItem('cid_backup', currentCid);
        
        // Set client companyId as the active cid/cookie
        localStorage.setItem('cid', companyId);
        setCookie('owner_user_id', companyId, 30);
        setCookie('cid', companyId, 30);

        setSelectedCompanyId(companyId);
        // Dispatch custom event to open the custom modal atom
        dispatchCustomEvent('modaldrafts-modal', { eventKey: 'drafts-modal' });
    };

    const handleCloseDraftsModal = () => {
        // Dispatch custom event to close the custom modal atom
        dispatchCustomEvent('modal-close-drafts-modal', {});
        setSelectedCompanyId(null);

        // Restore backup cid
        const backupCid = localStorage.getItem('cid_backup');
        if (backupCid !== null) {
            if (backupCid) {
                localStorage.setItem('cid', backupCid);
                setCookie('cid', backupCid, 30);
            } else {
                localStorage.removeItem('cid');
                deleteCookie('cid');
            }
            localStorage.removeItem('cid_backup');
        }

        // Clear owner_user_id cookie
        deleteCookie('owner_user_id');

        // Update gridKey to refresh counts
        setGridKey(prev => prev + 1);
    };

    const handleOpenAddClientModal = () => {
        dispatchCustomEvent('modaladd-client-modal', { eventKey: 'add-client-modal' });
    };

    const handleCloseAddClientModal = () => {
        dispatchCustomEvent('modal-close-add-client-modal', {});
    };

    const handleCreateClientSubmit = async (payload) => {
        const success = await accountantHub.handleCreateOfflineClient(payload);
        if (success) {
            handleCloseAddClientModal();
        }
    };

    return (
        <div className="hub-page">
            <ButtonGroup
                title={isInpersonated ? `Centro del Contador — ${profile?.companyName || "Cliente"}` : "Centro del Contador"}
                description="Gestiona tus clientes y audita sus documentos en tiempo real."
                onBack={isInpersonated ? unpersonate : () => window.location.replace('/hub')}
            >
                {isInpersonated ? (
                    <ButtonBasic 
                        type="outline-danger"
                        onClick={unpersonate}
                        title="Salir de Empresa"
                    />
                ) : (
                    <ModalBtn
                        id="manage-api-keys"
                        type="basic"
                        title="Gestionar API Keys"
                        icon={<Key size={18} />}
                    >
                        <div style={{ padding: '20px', minWidth: '600px' }}>
                            <h3>Gestión de API Keys</h3>
                            <p className="text-muted">Genera y administra llaves de acceso para integraciones externas.</p>
                            <ApiKeyManager />
                        </div>
                    </ModalBtn>
                )}
            </ButtonGroup>
            
            <main className="hub-content">
                {!isInpersonated && (
                    <div className="hub-status-row">
                        <ProviderStatus
                            status={healthStatus}
                            providerName="Estado de Red"
                        />
                        {suscripcion?.ai_tokens && (
                            <div className="ai-tokens-badge">
                                <span className="ai-icon">🧠</span>
                                <span>IA: {suscripcion.ai_tokens.usage.toLocaleString()} / {suscripcion.ai_tokens.is_unlimited ? '∞' : suscripcion.ai_tokens.limit.toLocaleString()} tokens</span>
                            </div>
                        )}
                    </div>
                )}

                <section className="hub-section">
                    <div className="section-header">
                        <h2 className="section-title">Clientes Activos</h2>
                        <p className="section-subtitle">Selecciona un cliente para trabajar en su POS o haz clic en "Auxiliar Contable" para auditar facturas.</p>
                    </div>
                    <LinkedCompaniesGrid 
                        key={gridKey}
                        linkedCompanies={accountantHub.linkedCompanies}
                        fiscalAlerts={accountantHub.fiscalAlerts}
                        handleEnterCompany={accountantHub.handleEnterCompany}
                        handleAlertClick={accountantHub.handleAlertClick}
                        isLoading={accountantHub.isLoading}
                        onOpenDrafts={(companyId) => {
                            handleOpenDraftsModal(companyId);
                        }} 
                        onAddClient={handleOpenAddClientModal}
                    />
                </section>
            </main>

            {/* Custom Modal Atom matching the design system of the app */}
            <Modal 
                id="drafts-modal" 
                onClose={handleCloseDraftsModal} 
                style={{ width: '95vw', maxWidth: '95vw', maxHeight: '95vh' }}
            >
                <div style={{ padding: '10px' }}>
                    <h3 style={{ margin: '0 0 20px 0', fontWeight: 800 }}>Auxiliar Contable: Documentos Pendientes</h3>
                    {selectedCompanyId && (
                        <PipelineDraftsManager profile={profile} companyId={selectedCompanyId} />
                    )}
                </div>
            </Modal>

            {/* Add Offline Client Modal */}
            <Modal 
                id="add-client-modal" 
                onClose={handleCloseAddClientModal} 
                style={{ width: '600px', maxWidth: '90vw' }}
            >
                <OfflineClientFormModal 
                    onSubmit={handleCreateClientSubmit} 
                    onClose={handleCloseAddClientModal}
                    isLoading={accountantHub.isLoading}
                />
            </Modal>
        </div>
    );
}

export default AccountantHubPage;
